import { useState } from 'react';
import { toast } from 'sonner';
import { useProperties } from '@/features/properties/queries';
import { ApiRequestError, apiErrorMessage } from '@/lib/api';
import { formatCents, formatIsoDate, formatRatio } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from 'cn';
import { LedgerTabs } from './LedgerTabs';
import { PrevisionChart } from './PrevisionChart';
import { useForecast, useGenerateAdvisory } from './queries';

function AlertsList({ alerts, currency }: { alerts: NonNullable<ReturnType<typeof useForecast>['data']>['alerts']; currency: string }) {
  const total = alerts.negativeCash.length + alerts.lowCoverage.length + alerts.chargeDrift.length;
  if (total === 0) {
    return <p className="text-sm text-muted-foreground">Aucune alerte détectée sur les 24 prochains mois.</p>;
  }

  return (
    <ul className="flex flex-col gap-2">
      {alerts.negativeCash.map((alert) => (
        <li key={`cash-${alert.monthStart}`} className="flex items-center gap-2 text-sm">
          <Badge variant="destructive">Trésorerie négative</Badge>
          {formatIsoDate(alert.monthStart)} : solde projeté {formatCents(alert.balanceCents, currency)}
        </li>
      ))}
      {alerts.lowCoverage.map((alert) => (
        <li key={`coverage-${alert.monthStart}`} className="flex items-center gap-2 text-sm">
          <Badge variant="destructive">Couverture insuffisante</Badge>
          {formatIsoDate(alert.monthStart)} : taux de {formatRatio(alert.ratio)}
        </li>
      ))}
      {alerts.chargeDrift.map((alert) => (
        <li key={`drift-${alert.propertyId}-${alert.categoryId}`} className="flex items-center gap-2 text-sm">
          <Badge variant="secondary">Charge en dérive</Badge>
          {alert.categoryLabel} : {formatCents(alert.previousYearCents, currency)} → {formatCents(alert.currentYearCents, currency)}
          {' '}({formatRatio(alert.changeRatio)})
        </li>
      ))}
    </ul>
  );
}

function AdvisoryCard({ propertyId }: { propertyId: number | undefined }) {
  const advisory = useGenerateAdvisory();
  const [notConfigured, setNotConfigured] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Commentaire rédigé automatiquement</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-xs text-muted-foreground">
          Rédigé à partir des chiffres calculés ci-dessus, sans nouveau chiffre — à vérifier avant toute décision.
        </p>
        <Button
          onClick={() => {
            setNotConfigured(false);
            advisory.mutate(propertyId, {
              onError: (error) => {
                if (error instanceof ApiRequestError && error.code === 'advisory_not_configured') {
                  setNotConfigured(true);
                  return;
                }
                toast.error(apiErrorMessage(error, 'Échec de la génération du commentaire'));
              },
            });
          }}
          disabled={advisory.isPending}
          className="w-fit"
        >
          {advisory.isPending ? 'Génération…' : 'Générer un commentaire'}
        </Button>

        {notConfigured && (
          <p className="text-sm text-muted-foreground">
            Rédaction assistée non configurée : renseignez <code className="font-mono text-xs">ANTHROPIC_API_KEY</code> côté serveur pour l'activer.
          </p>
        )}
        {advisory.data && <p className="text-sm">{advisory.data.commentary}</p>}
      </CardContent>
    </Card>
  );
}

function ForecastView({ propertyId, currency }: { propertyId: number | undefined; currency: string }) {
  const forecast = useForecast(propertyId);

  if (forecast.isPending) return <p className="text-muted-foreground">Chargement…</p>;
  if (forecast.isError) return <p className="text-destructive">Impossible de charger la prévision.</p>;

  const { series, alerts } = forecast.data;
  return (
    <div className="flex flex-col gap-6">
      <PrevisionChart months={series} currency={currency} />
      <Card>
        <CardHeader><CardTitle>Alertes</CardTitle></CardHeader>
        <CardContent><AlertsList alerts={alerts} currency={currency} /></CardContent>
      </Card>
      <AdvisoryCard propertyId={propertyId} />
    </div>
  );
}

export function PrevisionPage() {
  const [mode, setMode] = useState<'consolidated' | 'property'>('consolidated');
  const [propertyId, setPropertyId] = useState<string>('');
  const properties = useProperties();

  const selectedProperty = properties.data?.find((property) => property.id === Number(propertyId));
  const currency = mode === 'property' ? (selectedProperty?.currency ?? 'EUR') : 'EUR';

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold">Ledger</h1>
      <LedgerTabs />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-md border border-input p-0.5">
          <button
            type="button"
            onClick={() => setMode('consolidated')}
            className={cn('rounded px-3 py-1.5 text-sm font-medium', mode === 'consolidated' ? 'bg-accent' : 'text-muted-foreground')}
          >
            Consolidé
          </button>
          <button
            type="button"
            onClick={() => setMode('property')}
            className={cn('rounded px-3 py-1.5 text-sm font-medium', mode === 'property' ? 'bg-accent' : 'text-muted-foreground')}
          >
            Par bien
          </button>
        </div>

        {mode === 'property' && (
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger><SelectValue placeholder="Choisir un bien" /></SelectTrigger>
            <SelectContent>
              {properties.data?.map((property) => (
                <SelectItem key={property.id} value={String(property.id)}>{property.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {mode === 'consolidated' && <ForecastView propertyId={undefined} currency={currency} />}
      {mode === 'property' && propertyId !== '' && <ForecastView propertyId={Number(propertyId)} currency={currency} />}
      {mode === 'property' && propertyId === '' && <p className="text-muted-foreground">Choisissez un bien.</p>}
    </div>
  );
}
